import type { FerramentaOficinaCorte } from './oficinaCorte'

/**
 * Porta-malas: mesmo motor de vínculos e progresso da porta lateral
 * (`oficinaCorte.ts`), mas com uma estrutura mais simples — sem esquadria,
 * para-lama ou alternativas de criação de espaço. O plano (slides 25–26) só
 * pede tampa, trava, dobradiças e apoio; um contador único ou a reutilização
 * literal do tipo da porta lateral obrigaria a fingir vínculos que a tampa
 * não tem.
 *
 * O vidro traseiro fica fora desta etapa: no hatch ele acompanha a tampa e
 * teria que reagir à mesma abertura articulada, o que ainda não foi definido
 * com o professor. Tratar isso agora seria presumir uma regra que a porta
 * lateral não precisou presumir.
 */

export type IdVinculoTampa =
  | 'dobradicaEsquerda'
  | 'dobradicaDireita'
  | 'trava'
  | 'apoio'

export type EstadoVinculoTampa = 'integro' | 'liberado'
export type EstadoChapaTampa = 'integra' | 'deformada'

export interface EstadoTampaOficina {
  abertura: number
  chapa: EstadoChapaTampa
  vinculos: Record<IdVinculoTampa, EstadoVinculoTampa>
  sustentada: boolean
  removida: boolean
  testeManualRealizado: boolean
}

export type OperacaoTampa =
  | 'testarAberturaManual'
  | 'criarAbertura'
  | 'expandirTrava'
  | 'expandirDobradicaEsquerda'
  | 'expandirDobradicaDireita'
  | 'cortarDobradicaEsquerda'
  | 'cortarDobradicaDireita'
  | 'liberarApoio'
  | 'sustentarTampa'
  | 'removerTampa'

export type IdZonaOperacaoTampa =
  | 'chapa'
  | 'trava'
  | 'dobradicaEsquerda'
  | 'dobradicaDireita'
  | 'apoio'

export const ZONAS_POR_OPERACAO_TAMPA: Readonly<Record<OperacaoTampa, readonly IdZonaOperacaoTampa[]>> = {
  testarAberturaManual: [],
  criarAbertura: ['chapa', 'trava'],
  expandirTrava: ['trava'],
  expandirDobradicaEsquerda: ['dobradicaEsquerda'],
  expandirDobradicaDireita: ['dobradicaDireita'],
  cortarDobradicaEsquerda: ['dobradicaEsquerda'],
  cortarDobradicaDireita: ['dobradicaDireita'],
  liberarApoio: ['apoio'],
  sustentarTampa: [],
  removerTampa: [],
}

export const OPERACOES_TAMPA: readonly {
  id: OperacaoTampa
  nome: string
  instrucao: string
  usaFerramenta: boolean
}[] = [
  { id: 'testarAberturaManual', nome: 'Testar abertura manual', instrucao: 'Verifique primeiro se a tampa abre sem ferramenta.', usaFerramenta: false },
  { id: 'criarAbertura', nome: 'Criar abertura na fresta', instrucao: 'Apoie o alargador na fresta da tampa para criar o espaço inicial.', usaFerramenta: true },
  { id: 'expandirTrava', nome: 'Expandir no pino da trava', instrucao: 'Aplique o alargador na região da trava.', usaFerramenta: true },
  { id: 'expandirDobradicaEsquerda', nome: 'Expandir dobradiça esquerda', instrucao: 'Libere a dobradiça por expansão.', usaFerramenta: true },
  { id: 'expandirDobradicaDireita', nome: 'Expandir dobradiça direita', instrucao: 'Libere a dobradiça por expansão.', usaFerramenta: true },
  { id: 'cortarDobradicaEsquerda', nome: 'Cortar dobradiça esquerda', instrucao: 'Alternativa quando a expansão não for adotada.', usaFerramenta: true },
  { id: 'cortarDobradicaDireita', nome: 'Cortar dobradiça direita', instrucao: 'Alternativa quando a expansão não for adotada.', usaFerramenta: true },
  { id: 'liberarApoio', nome: 'Liberar o apoio a gás', instrucao: 'Corte o amortecedor a gás antes de manusear a tampa livremente.', usaFerramenta: true },
  { id: 'sustentarTampa', nome: 'Sustentar a tampa', instrucao: 'Prepare o recebimento da peça antes da retirada.', usaFerramenta: false },
  { id: 'removerTampa', nome: 'Retirar a tampa', instrucao: 'Conclua somente após liberar todos os vínculos.', usaFerramenta: false },
]

export interface ComandoOperacaoTampa {
  operacao: OperacaoTampa
  ferramenta?: FerramentaOficinaCorte
  zona?: IdZonaOperacaoTampa | null
  orientacaoValida?: boolean
}

export type CodigoResultadoOperacaoTampa =
  | 'concluida'
  | 'semProgresso'
  | 'ferramentaIncompativel'
  | 'aberturaInsuficiente'
  | 'vinculosRestantes'
  | 'tampaSemApoio'
  | 'tampaJaRemovida'
  | 'posicaoAusente'
  | 'zonaIncompativel'
  | 'orientacaoIncompativel'

export interface ResultadoOperacaoTampa {
  codigo: CodigoResultadoOperacaoTampa
  concluiu: boolean
  estado: EstadoTampaOficina
  mensagem: string
  alteracoes: readonly string[]
}

const FERRAMENTAS_INTERVENCAO: readonly FerramentaOficinaCorte[] = [
  'cortadorHidraulico',
  'alargador',
  'extensor',
  'hooligan',
]

const VINCULOS: readonly IdVinculoTampa[] = ['dobradicaEsquerda', 'dobradicaDireita', 'trava', 'apoio']

export function estadoInicialTampaOficina(): EstadoTampaOficina {
  return {
    abertura: 0,
    chapa: 'integra',
    vinculos: {
      dobradicaEsquerda: 'integro',
      dobradicaDireita: 'integro',
      trava: 'integro',
      apoio: 'integro',
    },
    sustentada: false,
    removida: false,
    testeManualRealizado: false,
  }
}

export function vinculosRestantesTampa(estado: EstadoTampaOficina): IdVinculoTampa[] {
  return VINCULOS.filter((id) => estado.vinculos[id] !== 'liberado')
}

export function tampaProntaParaRemocao(estado: EstadoTampaOficina): boolean {
  return vinculosRestantesTampa(estado).length === 0 && estado.sustentada && !estado.removida
}

function copiarEstado(estado: EstadoTampaOficina): EstadoTampaOficina {
  return { ...estado, vinculos: { ...estado.vinculos } }
}

function resultado(
  estado: EstadoTampaOficina,
  codigo: CodigoResultadoOperacaoTampa,
  mensagem: string,
  alteracoes: readonly string[] = [],
): ResultadoOperacaoTampa {
  return { codigo, concluiu: codigo === 'concluida', estado, mensagem, alteracoes }
}

function ferramentaAceita(
  ferramenta: FerramentaOficinaCorte | undefined,
  aceitas: readonly FerramentaOficinaCorte[],
): boolean {
  return ferramenta !== undefined && aceitas.includes(ferramenta)
}

/**
 * Executa uma única ação observável sobre a tampa. Mesmas garantias da
 * porta lateral: resultado parcial preservado, motivo explícito quando a
 * tentativa não avança, sem vidro nesta etapa (a intervenção nunca rompe
 * um vidro que a tampa ainda não modela).
 */
export function executarOperacaoTampa(
  atual: EstadoTampaOficina,
  comando: ComandoOperacaoTampa,
): ResultadoOperacaoTampa {
  const estado = copiarEstado(atual)
  if (estado.removida) {
    return resultado(estado, 'tampaJaRemovida', 'A tampa já foi removida neste exercício.')
  }

  if (comando.operacao === 'testarAberturaManual') {
    estado.testeManualRealizado = true
    if (estado.vinculos.trava === 'liberado') {
      estado.abertura = Math.max(estado.abertura, 0.35)
      return resultado(estado, 'concluida', 'A tampa abriu manualmente.', ['testeManualRealizado', 'abertura'])
    }
    return resultado(estado, 'semProgresso', 'Teste manual realizado: a tampa permanece presa pela trava.', ['testeManualRealizado'])
  }

  if (comando.operacao === 'sustentarTampa') {
    estado.sustentada = true
    return resultado(estado, 'concluida', 'A tampa está sustentada para a liberação final.', ['sustentada'])
  }

  if (comando.operacao === 'removerTampa') {
    const restantes = vinculosRestantesTampa(estado)
    if (restantes.length > 0) {
      return resultado(estado, 'vinculosRestantes', `A tampa ainda possui vínculos: ${restantes.join(', ')}.`)
    }
    if (!estado.sustentada) {
      return resultado(estado, 'tampaSemApoio', 'Sustente a tampa antes de concluir a retirada.')
    }
    estado.removida = true
    estado.abertura = 1
    return resultado(estado, 'concluida', 'Tampa removida e abertura liberada.', ['removida', 'abertura'])
  }

  const zonasAceitas = ZONAS_POR_OPERACAO_TAMPA[comando.operacao]
  if (zonasAceitas.length > 0 && !comando.zona) {
    return resultado(estado, 'posicaoAusente', 'Selecione um ponto de aplicação na tampa antes de acionar a ferramenta.')
  }
  if (comando.zona && !zonasAceitas.includes(comando.zona)) {
    return resultado(estado, 'zonaIncompativel', 'O ponto selecionado não corresponde à ação escolhida.')
  }
  if (zonasAceitas.length > 0 && comando.orientacaoValida === false) {
    return resultado(estado, 'orientacaoIncompativel', 'A ferramenta está em contato, mas precisa ser orientada corretamente antes do acionamento.')
  }

  if (!ferramentaAceita(comando.ferramenta, FERRAMENTAS_INTERVENCAO)) {
    return resultado(estado, 'ferramentaIncompativel', 'Esta ferramenta prepara a estabilização. Selecione uma ferramenta de intervenção para atuar na tampa.')
  }

  if (comando.operacao === 'criarAbertura') {
    if (!ferramentaAceita(comando.ferramenta, ['alargador', 'hooligan'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Use o alargador ou o Hooligan apoiado na fresta da tampa.')
    }
    estado.abertura = Math.max(estado.abertura, 0.14)
    estado.chapa = 'deformada'
    return resultado(estado, 'concluida', 'Abertura inicial criada na fresta da tampa.', ['abertura', 'chapa'])
  }

  if (estado.abertura < 0.1) {
    return resultado(estado, 'aberturaInsuficiente', 'Ainda não há espaço para posicionar a ferramenta neste ponto.')
  }

  const liberar = (id: IdVinculoTampa, mensagem: string): ResultadoOperacaoTampa => {
    estado.vinculos[id] = 'liberado'
    return resultado(estado, 'concluida', mensagem, [`vinculos.${id}`])
  }

  switch (comando.operacao) {
    case 'expandirTrava':
      if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
        return resultado(estado, 'ferramentaIncompativel', 'A expansão no pino da trava exige o alargador.')
      }
      estado.abertura = Math.max(estado.abertura, 0.45)
      return liberar('trava', 'A trava foi liberada por expansão.')
    case 'expandirDobradicaEsquerda':
    case 'expandirDobradicaDireita': {
      if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
        return resultado(estado, 'ferramentaIncompativel', 'A expansão da dobradiça exige o alargador.')
      }
      const id = comando.operacao === 'expandirDobradicaEsquerda' ? 'dobradicaEsquerda' : 'dobradicaDireita'
      return liberar(id, 'A dobradiça foi liberada por expansão.')
    }
    case 'cortarDobradicaEsquerda':
    case 'cortarDobradicaDireita': {
      if (!ferramentaAceita(comando.ferramenta, ['cortadorHidraulico'])) {
        return resultado(estado, 'ferramentaIncompativel', 'O corte da dobradiça exige o cortador hidráulico.')
      }
      const id = comando.operacao === 'cortarDobradicaEsquerda' ? 'dobradicaEsquerda' : 'dobradicaDireita'
      return liberar(id, 'A dobradiça foi cortada como alternativa de liberação.')
    }
    case 'liberarApoio':
      if (!ferramentaAceita(comando.ferramenta, ['cortadorHidraulico'])) {
        return resultado(estado, 'ferramentaIncompativel', 'Use o cortador hidráulico no apoio a gás.')
      }
      return liberar('apoio', 'O apoio a gás foi liberado.')
    default:
      return resultado(estado, 'semProgresso', 'A operação não alterou o estado da tampa.')
  }
}
